import { useState, useMemo } from 'react';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

type TransactionType = 'all' | 'income' | 'expense' | 'transfer';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export function useTransactionFilters(transactions: Transaction[]) {
  const [filter, setFilter] = useState<TransactionType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });

  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter((t) => t.type === filter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.description?.toLowerCase().includes(query) ||
          t.category?.name?.toLowerCase().includes(query) ||
          t.account?.name?.toLowerCase().includes(query) ||
          t.to_account?.name?.toLowerCase().includes(query)
      );
    }

    // Filter by date range
    if (dateRange.startDate) {
      const start = new Date(dateRange.startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter((t) => {
        const transactionDate = new Date(t.date);
        return transactionDate >= start;
      });
    }

    if (dateRange.endDate) {
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((t) => {
        const transactionDate = new Date(t.date);
        return transactionDate <= end;
      });
    }

    return filtered;
  }, [transactions, filter, searchQuery, dateRange]);

  const clearDateRange = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  return {
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    clearDateRange,
    filteredTransactions,
  };
}
